import '@banno/jha-wc/src/jha-tab-item/jha-tab-item.js';
import '@banno/jha-wc/src/jha-tab/jha-tab.js';
import { css, html } from 'lit';
import { customElement, property, queryAssignedElements, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { TmBaseComponent } from '../tm-base.component';

export type TmTabItem = {
    label: string;
    href?: string;
    disabled?: boolean;
    active?: boolean;
    onClick?: (tab: TmTabItem, evt: Event) => void;
};

export abstract class TmWizardStep extends TmBaseComponent {
    @property({ type: Boolean, reflect: true })
    public active = false;

    public abstract stepLabel: string;

    public abstract validate(): boolean;
}

@customElement('tm-wizard')
export class TmWizard extends TmBaseComponent {
    @property({ type: Boolean })
    public wizardMode: boolean = true;

    @property({ type: Number })
    public activeTabIndex = 0;

    @property({ type: Boolean, reflect: true })
    public loading = false;

    @queryAssignedElements()
    private readonly controls?: TmWizardStep[];

    @state()
    private greatestTabIndexCompleted = this.activeTabIndex;

    @state()
    private slideDirection?: 'next' | 'previous';

    private wizardSteps = this.controls?.filter(c => c instanceof TmWizardStep);

    private get isDisabled() {
        if (this.loading) {
            return true;
        }

        return this.wizardMode && this.activeTabIndex > this.greatestTabIndexCompleted;
    }

    private get stepLabels() {
        if (!this.wizardSteps) {
            return [];
        }
        return this.wizardSteps?.map(s => s.stepLabel);
    }

    private get contentClasses() {
        return {
            'content-container': true,
            next: this.slideDirection === 'next',
            previous: this.slideDirection === 'previous',
        };
    }

    public render() {
        return html`
            ${this.renderTabContainer()}
            <div class=${classMap(this.contentClasses)}>
                <slot @slotchange=${this.handleSlotChange}></slot>
            </div>
        `;
    }

    protected updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('activeTabIndex')) {
            if (this.wizardMode) {
                this.greatestTabIndexCompleted = Math.max(
                    this.greatestTabIndexCompleted,
                    this.activeTabIndex
                );
            }
            this.checkSlideDirection();
            this.showActiveStep();
        }
    }

    private renderTabContainer() {
        return html`<jha-tab class="jha-tab mb-5 flex flex-row justify-between" bordered
            >${this.renderTab()}</jha-tab
        >`;
    }

    private renderTab() {
        const tabWidth = `w-1/${this.stepLabels?.length ?? 1}`;
        return this.stepLabels?.map(
            (label, index) =>
                html`<jha-tab-item
                    class=${classMap({
                        [tabWidth]: true,
                    })}
                    ?disabled=${this.loading || this.isDisabled}
                    @click=${(e: MouseEvent) => this.handleTabClick(e, index)}
                    ?active=${index === this.activeTabIndex}
                >
                    ${this.renderLabel(label, index)}
                </jha-tab-item>`
        );
    }

    private renderLabel(label: string, index: number) {
        const active = index === this.activeTabIndex;
        const labelContent = this.wizardMode ? `${index + 1}. ${label}` : label;
        if (this.loading && active) {
            return html`<div class="flex flex-row gap-2 items-center">
                <jha-progress></jha-progress>
                <span>${label}</span>
            </div>`;
        }
        return labelContent;
    }

    private checkSlideDirection() {
        const oldTab = this.activeTabIndex - 1;
        if (oldTab >= 0) {
            this.slideDirection = this.activeTabIndex > oldTab ? 'next' : 'previous';
        }
    }

    private showActiveStep() {
        this.wizardSteps?.forEach((step, index) => {
            step.active = index === this.activeTabIndex;
        });
    }

    private handleSlotChange() {
        this.wizardSteps = this.controls?.filter(c => c instanceof TmWizardStep);
        this.showActiveStep();
        this.requestUpdate();
    }

    private handleTabClick(e: MouseEvent, index: number) {
        // Disable tab that is not yet available
        if (this.wizardMode && index > this.greatestTabIndexCompleted) {
            return;
        }
        this.activeTabIndex = index;
        this.greatestTabIndexCompleted = Math.max(this.greatestTabIndexCompleted, index);
        this.dispatchEvent(new CustomEvent('tab-change', { detail: { index } }));
    }

    public static get styles() {
        return css`
            :host {
                --jha-tab-item-padding: 0;
                --jha-tab-item-text-align: left;
                display: flex;
                flex-direction: column;
            }

            .content-container {
                position: relative;
                overflow: hidden;
                display: grid;
                flex: 1;
            }

            /* 
             * --- Core Animation Setup ---
             * 1. All slotted items are stacked in the same grid cell.
             * 2. A transition is enabled for transform and opacity changes.
             */
            ::slotted(*) {
                grid-area: 1 / 1;
                transition:
                    transform 300ms ease-in-out,
                    opacity 300ms ease-in-out;
            }

            /* 
             * --- Slide Animation Logic ---
             * This creates a "carousel" effect where content shifts left for "next"
             * and right for "previous".
             *
             * How it works:
             * - The active slide has default styles (visible and centered).
             * - Inactive slides are made transparent and moved off-screen.
             * - When the active slide changes, the new slide transitions from its
             *   off-screen position to the center, and the old slide transitions
             *   from the center to the same off-screen position.
             */

            /* Base state for all inactive slides: transparent and non-interactive. */
            ::slotted(:not([active])) {
                opacity: 0;
                pointer-events: none;
            }

            /* 
             * For a FORWARD ("next") navigation, inactive slides are positioned
             * to the LEFT. This causes the outgoing slide to move left, and the
             * incoming slide to arrive from the left.
             */
            .content-container.next ::slotted(:not([active])) {
                transform: translateX(-100%);
            }

            /* 
             * For a BACKWARD ("previous") navigation, inactive slides are positioned
             * to the RIGHT, creating a rightward shift.
             */
            .content-container.previous ::slotted(:not([active])) {
                transform: translateX(100%);
            }

            jha-tab[bordered] {
                border-bottom: 1px solid
                    var(--jha-tab-border-color, var(--jha-border-color, #e4e7ea));
            }

            jha-tab[bordered] > jha-tab-item {
                border-bottom-width: 1px;
                border-bottom-color: transparent;
            }

            jha-tab[bordered] > jha-tab-item[active],
            jha-tab[bordered] > jha-tab-item[completed] {
                border-bottom: 1px solid var(--jha-tab-item-border-color, blue);
                border-bottom-color: var(--jha-tab-item-border-color, blue);
            }

            jha-tab[bordered] > jha-tab-item[disabled] {
                color: grey;
                --jha-tab-item-hover-color: grey;
                border-bottom: 1px solid transparent;
                border-bottom-color: transparent;
            }
        `;
    }
}
